import React, { useMemo, useState, useCallback } from "react";
import "./../Styles/calculate.css";
import HorizontalAds from "../../Admin/Pages/HorizontalAds";

/* =========================================================
   INITIAL ROW SCHEMAS
========================================================= */
const INITIAL_EMBROIDERY_ROW = {
  section: "Section 1",
  area: 0,
  stitch: 0,
  heads: 0,
  rate: 0,
};

const INITIAL_LACE_ROW = {
  section: "Section 1",
  area: 0,
  stitch: 0,
  heads: 0,
  lacesPerHead: 1,
  rate: 0,
};

export default function Calculate() {
  /* =========================================================
     1. EMBROIDERY WORK STATE & HANDLERS
  ========================================================= */
  const [embroideryRows, setEmbroideryRows] = useState([
    { ...INITIAL_EMBROIDERY_ROW },
  ]);

  const addEmbroideryRow = useCallback(() => {
    setEmbroideryRows((prev) => [
      ...prev,
      {
        ...INITIAL_EMBROIDERY_ROW,
        section: `Section ${prev.length + 1}`,
      },
    ]);
  }, []);

  const removeEmbroideryRow = useCallback((index) => {
    setEmbroideryRows((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const clearEmbroideryRows = useCallback(() => {
    setEmbroideryRows([]);
  }, []);

  const updateEmbroideryRow = useCallback((index, field, value) => {
    setEmbroideryRows((prev) =>
      prev.map((row, i) => (i === index ? { ...row, [field]: value } : row)),
    );
  }, []);

  /* =========================================================
     2. LACE COSTING STATE & HANDLERS
  ========================================================= */
  const [laceRows, setLaceRows] = useState([{ ...INITIAL_LACE_ROW }]);

  const addLaceRow = useCallback(() => {
    setLaceRows((prev) => [
      ...prev,
      {
        ...INITIAL_LACE_ROW,
        section: `Section ${prev.length + 1}`,
      },
    ]);
  }, []);

  const removeLaceRow = useCallback((index) => {
    setLaceRows((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const clearLaceRows = useCallback(() => {
    setLaceRows([]);
  }, []);

  const updateLaceRow = useCallback((index, field, value) => {
    setLaceRows((prev) =>
      prev.map((row, i) => (i === index ? { ...row, [field]: value } : row)),
    );
  }, []);

  /* Helper to clear 0 on focus */
  const handleFocus = (index, field, value, updateFn, defaultValue = 0) => {
    if (Number(value) === defaultValue || value === "0" || value === 0) {
      updateFn(index, field, "");
    }
  };

  /* Helper to restore default on blur if empty */
  const handleBlur = (index, field, value, updateFn, defaultValue = 0) => {
    if (value === "" || value === null || value === undefined) {
      updateFn(index, field, defaultValue);
    }
  };

  /* =========================================================
     EMBROIDERY CALCULATION
  ========================================================= */
  const embroideryCalc = useMemo(() => {
    let totalStitch = 0;
    let totalArea = 0;
    let totalHeads = 0;
    let totalCost = 0;
    let totalMeter = 0;

    const enriched = embroideryRows.map((row) => {
      const stitch = Number(row.stitch) || 0;
      const heads = Number(row.heads) || 0;
      const area = Number(row.area) || 0;
      const rate = Number(row.rate) || 0;

      const cost = (stitch * heads * rate) / 1000;
      const workMeter = heads * (area / 1000);

      totalStitch += stitch;
      totalArea += area;
      totalHeads += heads;
      totalCost += cost;
      totalMeter += workMeter;

      return { ...row, cost, workMeter };
    });

    return {
      rows: enriched,
      totalStitch,
      totalArea,
      totalHeads,
      totalCost,
      totalMeter,
    };
  }, [embroideryRows]);

  /* =========================================================
     LACE CALCULATION
  ========================================================= */
  const laceCalc = useMemo(() => {
    let totalStitch = 0;
    let totalArea = 0;
    let totalHeads = 0;
    let totalLaces = 0;
    let totalCost = 0;
    let totalMeter = 0;

    const enriched = laceRows.map((row) => {
      const stitch = Number(row.stitch) || 0;
      const heads = Number(row.heads) || 0;
      const lacesPerHead = Number(row.lacesPerHead) || 0;
      const area = Number(row.area) || 0;
      const rate = Number(row.rate) || 0;

      const cost = (stitch * heads * lacesPerHead * rate) / 1000;

      const workMeter = heads * lacesPerHead * (area / 1000);

      totalStitch += stitch;
      totalArea += area;
      totalHeads += heads;
      totalLaces += lacesPerHead;
      totalCost += cost;
      totalMeter += workMeter;

      return {
        ...row,
        cost,
        workMeter,
      };
    });

    // Cost per meter
    const permetercost = totalMeter > 0 ? totalCost / totalMeter : 0;

    return {
      rows: enriched,
      totalStitch,
      totalArea,
      totalHeads,
      totalLaces,
      totalCost,
      totalMeter,
      permetercost,
    };
  }, [laceRows]);

  return (
    <div className="calc-wrapper">
      {/* =========================================================
          SECTION 1: EMBROIDERY WORK COSTING
      ========================================================= */}
      <div className="calc-main-layout">
        <div className="calc-right" data-aos="fade-left">
          <div className="calc-body-grid">
            <div className="image-container big-image">
              <img
                src="images/calculate/embroiderywork.jpg"
                alt="Dress calculation"
              />
            </div>

            <div className="table-box" data-aos="fade-up" data-aos-delay="200">
              <div className="table-header-actions">
                <h3>Calculate Embroidery Work Costing</h3>
                <div
                  className="action-buttons-group"
                  style={{ display: "flex", gap: "10px" }}
                >
                  {embroideryRows.length > 0 && (
                    <button
                      className="clear-btn"
                      onClick={clearEmbroideryRows}
                      style={{
                        backgroundColor: "#dc3545",
                        color: "#fff",
                        border: "none",
                        padding: "6px 12px",
                        borderRadius: "4px",
                        cursor: "pointer",
                      }}
                    >
                      Clear All
                    </button>
                  )}
                  <button className="add-btn" onClick={addEmbroideryRow}>
                    <span>+</span> Add Section
                  </button>
                </div>
              </div>

              <div className="table-responsive">
                <table>
                  <thead>
                    <tr>
                      <th>Section</th>
                      <th>
                        Area <small>(mm)</small>
                      </th>
                      <th>Stitches</th>
                      <th>Heads</th>
                      <th>
                        Rate <small>(₹/1000)</small>
                      </th>
                      <th>Cost</th>
                      <th>Meter</th>
                      <th style={{ width: "50px" }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {embroideryCalc.rows.map((row, i) => (
                      <tr
                        key={i}
                        style={{ position: "relative" }}
                        data-aos="fade-up"
                        data-aos-delay={i * 80}
                      >
                        <td>
                          <input
                            className="table-input"
                            type="text"
                            value={row.section}
                            onChange={(e) =>
                              updateEmbroideryRow(i, "section", e.target.value)
                            }
                          />
                        </td>
                        <td>
                          <input
                            className="table-input text-right"
                            type="text"
                            step="0.01"
                            min="0"
                            value={row.area}
                            placeholder="0"
                            onFocus={() =>
                              handleFocus(
                                i,
                                "area",
                                row.area,
                                updateEmbroideryRow,
                              )
                            }
                            onBlur={() =>
                              handleBlur(
                                i,
                                "area",
                                row.area,
                                updateEmbroideryRow,
                              )
                            }
                            onChange={(e) =>
                              updateEmbroideryRow(i, "area", e.target.value)
                            }
                          />
                        </td>
                        <td>
                          <input
                            className="table-input text-right"
                            type="text"
                            min="0"
                            value={row.stitch}
                            placeholder="0"
                            onFocus={() =>
                              handleFocus(
                                i,
                                "stitch",
                                row.stitch,
                                updateEmbroideryRow,
                              )
                            }
                            onBlur={() =>
                              handleBlur(
                                i,
                                "stitch",
                                row.stitch,
                                updateEmbroideryRow,
                              )
                            }
                            onChange={(e) =>
                              updateEmbroideryRow(i, "stitch", e.target.value)
                            }
                          />
                        </td>
                        <td>
                          <input
                            className="table-input text-right"
                            type="text"
                            min="0"
                            value={row.heads}
                            placeholder="0"
                            onFocus={() =>
                              handleFocus(
                                i,
                                "heads",
                                row.heads,
                                updateEmbroideryRow,
                              )
                            }
                            onBlur={() =>
                              handleBlur(
                                i,
                                "heads",
                                row.heads,
                                updateEmbroideryRow,
                              )
                            }
                            onChange={(e) =>
                              updateEmbroideryRow(i, "heads", e.target.value)
                            }
                          />
                        </td>
                        <td>
                          <div className="table-rate-input">
                            <span>₹</span>
                            <input
                              className="table-input text-right"
                              type="text"
                              step="0.01"
                              min="0"
                              value={row.rate}
                              placeholder="0"
                              onFocus={() =>
                                handleFocus(
                                  i,
                                  "rate",
                                  row.rate,
                                  updateEmbroideryRow,
                                )
                              }
                              onBlur={() =>
                                handleBlur(
                                  i,
                                  "rate",
                                  row.rate,
                                  updateEmbroideryRow,
                                )
                              }
                              onChange={(e) =>
                                updateEmbroideryRow(i, "rate", e.target.value)
                              }
                            />
                          </div>
                        </td>
                        <td
                          className="cost-cell"
                          style={{ paddingRight: "10px" }}
                        >
                          ₹{row.cost.toFixed(2)}
                        </td>
                        <td className="meter-cell">
                          {row.workMeter.toFixed(2)}
                        </td>
                        <td
                          style={{
                            width: "50px",
                            textAlign: "center",
                            verticalAlign: "middle",
                          }}
                        >
                          <button
                            type="button"
                            onClick={() => removeEmbroideryRow(i)}
                            title="Remove row"
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              justifyContent: "center",
                              backgroundColor: "transparent",
                              color: "#dc3545",
                              border: "none",
                              fontWeight: "800",
                              cursor: "pointer",
                              fontSize: "20px",
                              lineHeight: "1",
                            }}
                          >
                            <i className="bi bi-x-circle"></i>
                          </button>
                        </td>
                      </tr>
                    ))}

                    {embroideryCalc.rows.length === 0 && (
                      <tr>
                        <td
                          colSpan="8"
                          style={{
                            textAlign: "center",
                            padding: "20px",
                            color: "#888",
                          }}
                        >
                          No sections added. Click "Add Section" to get started.
                        </td>
                      </tr>
                    )}
                  </tbody>

                  {embroideryCalc.rows.length > 0 && (
                    <tfoot>
                      <tr>
                        <th>Total</th>
                        <th>{embroideryCalc.totalArea.toFixed(2)}</th>
                        <th>{embroideryCalc.totalStitch.toLocaleString()}</th>
                        <th>{embroideryCalc.totalHeads}</th>
                        <th>—</th>
                        <th>₹{embroideryCalc.totalCost.toFixed(2)}</th>
                        <th>{embroideryCalc.totalMeter.toFixed(2)}</th>
                        <th></th>
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>
            </div>

            {/* EMBROIDERY SUMMARY */}
            <div
              className="summary-box"
              data-aos="fade-left"
              data-aos-delay="300"
            >
              <h3>Summary Overview</h3>
              <div className="summary-cards-grid">
                <div
                  className="card highlight"
                  data-aos="flip-left"
                  data-aos-delay="300"
                >
                  <span className="card-label">Total Cost</span>
                  <h2 className="card-value highlight-value">
                    ₹{embroideryCalc.totalCost.toFixed(2)}
                  </h2>
                </div>
                <div
                  className="card"
                  data-aos="flip-right"
                  data-aos-delay="450"
                >
                  <span className="card-label">Total Meter</span>
                  <h4 className="card-value">
                    {embroideryCalc.totalMeter.toFixed(2)}
                    <small> Mtr</small>
                  </h4>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* =========================================================
          SECTION 2: LACE COSTING
      ========================================================= */}
      <div className="calc-main-layout">
        <div className="calc-right" data-aos="fade-left">
          <div className="calc-body-grid">
            <div className="image-container big-image">
              <img src="images/calculate/less.jpg" alt="Lace calculation" />
            </div>

            <div className="table-box" data-aos="fade-up" data-aos-delay="200">
              <div className="table-header-actions">
                <h3>Calculate Lace Costing</h3>
                <div
                  className="action-buttons-group"
                  style={{ display: "flex", gap: "10px" }}
                >
                  {laceRows.length > 0 && (
                    <button
                      className="clear-btn"
                      onClick={clearLaceRows}
                      style={{
                        backgroundColor: "#dc3545",
                        color: "#fff",
                        border: "none",
                        padding: "6px 12px",
                        borderRadius: "4px",
                        cursor: "pointer",
                      }}
                    >
                      Clear All
                    </button>
                  )}
                  <button className="add-btn" onClick={addLaceRow}>
                    <span>+</span> Add Section
                  </button>
                </div>
              </div>

              <div className="table-responsive">
                <table>
                  <thead>
                    <tr>
                      <th>Section</th>
                      <th>
                        Area <small>(mm)</small>
                      </th>
                      <th>Stitches</th>
                      <th>Heads</th>
                      <th>Laces(Per Head)</th>
                      <th>
                        Rate <small>(₹/1000)</small>
                      </th>
                      <th>Cost</th>
                      <th>Meter</th>
                      <th style={{ width: "50px" }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {laceCalc.rows.map((row, i) => (
                      <tr
                        key={i}
                        style={{ position: "relative" }}
                        data-aos="fade-up"
                        data-aos-delay={i * 80}
                      >
                        <td>
                          <input
                            className="table-input"
                            type="text"
                            value={row.section}
                            onChange={(e) =>
                              updateLaceRow(i, "section", e.target.value)
                            }
                          />
                        </td>
                        <td>
                          <input
                            className="table-input text-right"
                            type="text"
                            step="0.01"
                            min="0"
                            value={row.area}
                            placeholder="0"
                            onFocus={() =>
                              handleFocus(i, "area", row.area, updateLaceRow)
                            }
                            onBlur={() =>
                              handleBlur(i, "area", row.area, updateLaceRow)
                            }
                            onChange={(e) =>
                              updateLaceRow(i, "area", e.target.value)
                            }
                          />
                        </td>
                        <td>
                          <input
                            className="table-input text-right"
                            type="text"
                            min="0"
                            value={row.stitch}
                            placeholder="0"
                            onFocus={() =>
                              handleFocus(
                                i,
                                "stitch",
                                row.stitch,
                                updateLaceRow,
                              )
                            }
                            onBlur={() =>
                              handleBlur(i, "stitch", row.stitch, updateLaceRow)
                            }
                            onChange={(e) =>
                              updateLaceRow(i, "stitch", e.target.value)
                            }
                          />
                        </td>
                        <td>
                          <input
                            className="table-input text-right"
                            type="text"
                            min="0"
                            value={row.heads}
                            placeholder="0"
                            onFocus={() =>
                              handleFocus(i, "heads", row.heads, updateLaceRow)
                            }
                            onBlur={() =>
                              handleBlur(i, "heads", row.heads, updateLaceRow)
                            }
                            onChange={(e) =>
                              updateLaceRow(i, "heads", e.target.value)
                            }
                          />
                        </td>
                        <td>
                          <input
                            className="table-input text-right"
                            type="text"
                            min="0"
                            value={row.lacesPerHead}
                            placeholder="1"
                            onFocus={() =>
                              handleFocus(
                                i,
                                "lacesPerHead",
                                row.lacesPerHead,
                                updateLaceRow,
                                1,
                              )
                            }
                            onBlur={() =>
                              handleBlur(
                                i,
                                "lacesPerHead",
                                row.lacesPerHead,
                                updateLaceRow,
                                1,
                              )
                            }
                            onChange={(e) =>
                              updateLaceRow(i, "lacesPerHead", e.target.value)
                            }
                          />
                        </td>
                        <td>
                          <div className="table-rate-input">
                            <span>₹</span>
                            <input
                              className="table-input text-right"
                              type="text"
                              step="0.01"
                              min="0"
                              value={row.rate}
                              placeholder="0"
                              onFocus={() =>
                                handleFocus(i, "rate", row.rate, updateLaceRow)
                              }
                              onBlur={() =>
                                handleBlur(i, "rate", row.rate, updateLaceRow)
                              }
                              onChange={(e) =>
                                updateLaceRow(i, "rate", e.target.value)
                              }
                            />
                          </div>
                        </td>
                        <td
                          className="cost-cell"
                          style={{ paddingRight: "10px" }}
                        >
                          ₹{row.cost.toFixed(2)}
                        </td>
                        <td className="meter-cell">
                          {row.workMeter.toFixed(2)}
                        </td>
                        <td
                          style={{
                            width: "50px",
                            textAlign: "center",
                            verticalAlign: "middle",
                          }}
                        >
                          <button
                            type="button"
                            onClick={() => removeLaceRow(i)}
                            title="Remove row"
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              justifyContent: "center",
                              backgroundColor: "transparent",
                              color: "#dc3545",
                              border: "none",
                              fontWeight: "800",
                              cursor: "pointer",
                              fontSize: "20px",
                              lineHeight: "1",
                            }}
                          >
                            <i className="bi bi-x-circle"></i>
                          </button>
                        </td>
                      </tr>
                    ))}

                    {laceCalc.rows.length === 0 && (
                      <tr>
                        <td
                          colSpan="9"
                          style={{
                            textAlign: "center",
                            padding: "20px",
                            color: "#888",
                          }}
                        >
                          No sections added. Click "Add Section" to get started.
                        </td>
                      </tr>
                    )}
                  </tbody>

                  {laceCalc.rows.length > 0 && (
                    <tfoot>
                      <tr>
                        <th>Total</th>
                        <th>{laceCalc.totalArea.toFixed(2)}</th>
                        <th>{laceCalc.totalStitch.toLocaleString()}</th>
                        <th>{laceCalc.totalHeads}</th>
                        <th>{laceCalc.totalLaces}</th>
                        <th>—</th>
                        <th>₹{laceCalc.totalCost.toFixed(2)}</th>
                        <th>{laceCalc.totalMeter.toFixed(2)}</th>
                        <th></th>
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>
            </div>

            {/* LACE SUMMARY */}
            <div
              className="summary-box"
              data-aos="fade-left"
              data-aos-delay="300"
            >
              <h3>Summary Overview</h3>
              <div className="summary-cards-grid">
                <div
                  className="card highlight"
                  data-aos="flip-left"
                  data-aos-delay="300"
                >
                  <span className="card-label">Total Cost</span>
                  <h2 className="card-value highlight-value">
                    ₹{laceCalc.totalCost.toFixed(2)}
                  </h2>
                </div>
                <div
                  className="card"
                  data-aos="flip-right"
                  data-aos-delay="450"
                >
                  <span className="card-label">Total Meter</span>
                  <h4 className="card-value">
                    {laceCalc.totalMeter.toFixed(2)}
                    <small> Mtr</small>
                  </h4>
                </div>

                <div
                  className="card"
                  data-aos="flip-right"
                  data-aos-delay="450"
                >
                  <span className="card-label">Per Meter Cost</span>
                  <h4 className="card-value">
                    ₹{laceCalc.permetercost.toFixed(2)}
                  </h4>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
